import { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface ShareResponse {
  message: string;
  shared: string | null;
  recipients: Array<{
    id: number;
    email: string;
    emailDelivered: boolean;
  }>;
}

/**
 * Phase K — Share Structure modal. Posts to
 * `POST /api/v1/structures/{id}/share` with the chosen options.
 * Modeled on the legacy Bootstrap "Share Structure" modal.
 */
export function ShareStructureModal({
  structureId,
  ownerEmail,
  mode = 'share',
  onClose,
}: {
  structureId: number;
  ownerEmail: string | null;
  /**
   * `'share'` (default) mirrors the legacy "Share Or Submit" action;
   * `'quote'` mirrors "Request A Quote", which pre-checks "Notify the ADS
   * sales team" (legacy `toggle-checkbox-quote`).
   */
  mode?: 'share' | 'quote';
  onClose: () => void;
}) {
  const [sendToAccount, setSendToAccount] = useState(true);
  const [shareToOther, setShareToOther] = useState(false);
  const [emails, setEmails] = useState<string[]>(['']);
  const [sendToAds, setSendToAds] = useState(mode === 'quote');
  const [tosAgree, setTosAgree] = useState(false);
  const [optInNewsletter, setOptInNewsletter] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const updateEmail = (index: number, value: string) => {
    setEmails((prev) => prev.map((e, i) => (i === index ? value : e)));
  };

  const removeEmail = (index: number) => {
    setEmails((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : ['']));
  };

  const addEmail = () => setEmails((prev) => [...prev, '']);

  const submit = async () => {
    if (!tosAgree) {
      setError('We require you agree to these terms before sharing.');
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const recipients = shareToOther
        ? emails.map((x) => x.trim()).filter(Boolean)
        : [];
      const res = await api.post<ShareResponse>(`/api/v1/structures/${structureId}/share`, {
        sendToAccount,
        sendToAds,
        recipients,
        optInNewsletter,
      });
      setResult(res.message || 'Structure shared.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not share structure');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal d-block"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-structure-title"
      tabIndex={-1}
    >
      <div
        className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn-close p-2"
              aria-label="Close"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body mt-0 pt-0 m-3 px-4">
            <h1 id="share-structure-title" className="text-center">
              Share Your Structure
            </h1>
            <p className="text-center">
              Please choose from the options below to share or submit your&nbsp;structure
            </p>

            <div className="row justify-content-center">
              <div className="col-12 col-md-6 px-md-3 pt-1">
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="send_to_account"
                    checked={sendToAccount}
                    onChange={(e) => setSendToAccount(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="send_to_account">
                    Send to my account email
                  </label>
                </div>
                {ownerEmail && (
                  <div className="d-block d-md-none pb-3">
                    <label className="text-black-50">{ownerEmail}</label>
                  </div>
                )}
              </div>
              <div className="col-6 d-none d-md-block pt-1 text-end">
                {ownerEmail && <label className="text-black-50">{ownerEmail}</label>}
              </div>

              <div className="col-12 px-md-3 pt-1">
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="send_to_email"
                    checked={shareToOther}
                    onChange={(e) => setShareToOther(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="send_to_email">
                    Share to another email address
                  </label>
                </div>
              </div>

              <div className="col-12 px-md-3 pt-1">
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="send_to_ADS"
                    checked={sendToAds}
                    onChange={(e) => setSendToAds(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="send_to_ADS">
                    Send to ADS for quoting
                  </label>
                </div>
              </div>

              {shareToOther && (
                <div className="col-12 px-md-3 pt-1">
                  <div>
                    {emails.map((email, index) => (
                      <div className="d-flex py-1 align-items-center" key={index}>
                        <div className="flex-grow-1">
                          <div className="input-group flex-nowrap">
                            <input
                              className="form-control"
                              type="text"
                              value={email}
                              onChange={(e) => updateEmail(index, e.target.value)}
                              placeholder="Email Address"
                            />
                          </div>
                        </div>
                        <div>
                          <a
                            href="#"
                            className="text-reset"
                            onClick={(e) => {
                              e.preventDefault();
                              removeEmail(index);
                            }}
                            aria-label="Remove email"
                          >
                            <img className="mx-auto ps-3" src="/images/ICON/Icon_Trash.png" alt="" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="d-flex align-items-center justify-content-center">
                    <button
                      type="button"
                      className="btn btn-outline-medium mb-2"
                      onClick={addEmail}
                    >
                      <i className="fa-solid fa-circle-plus"></i> Add Another Email&nbsp;Address
                    </button>
                  </div>
                </div>
              )}

              <div className="col-12 p-3 mt-3 border-top">
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="tos_agree"
                    checked={tosAgree}
                    onChange={(e) => setTosAgree(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="tos_agree">
                    I agree to the{' '}
                    <a
                      href="https://www.adspipe.com/terms-conditions/terms-and-conditions-sales"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms&nbsp;&amp;&nbsp;Conditions
                    </a>
                  </label>
                </div>
              </div>

              <div className="col-12 p-3 border-top">
                <div className="form-check ps-3">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="newsletter"
                    checked={optInNewsletter}
                    onChange={(e) => setOptInNewsletter(e.target.checked)}
                  />
                  <label className="form-check-label fw-normal" htmlFor="newsletter">
                    Sign me up to receive the latest news and product information.
                  </label>
                </div>
              </div>

              {error && (
                <div className="col-12 text-center">
                  <p className="text-danger mb-2">{error}</p>
                </div>
              )}
              {result && (
                <div className="col-12 text-center">
                  <p className="text-success mb-2">{result}</p>
                </div>
              )}

              <div className="col-12 text-center">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={submit}
                  disabled={submitting}
                >
                  <img className="img-fluid" src="/images/ICON/Icon_Share.png" alt="" />{' '}
                  {submitting ? 'Sending…' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
